import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSupportForm, getSupportCategories } from '../utils/support';

interface SupportTicketFormProps {
  onSubmit?: (category: string, message: string) => void;
  isLoading?: boolean;
}

export default function SupportTicketForm({ onSubmit, isLoading = false }: SupportTicketFormProps) {
  const { t } = useTranslation();
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateSupportForm(category, message);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setErrors({});
    onSubmit?.(category, message);
    setCategory('');
    setMessage('');
  };

  const categories = getSupportCategories();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          {t('support.category')} <span className="text-red-500">*</span>
        </label>
        <select
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setErrors({ ...errors, category: '' });
          }}
          className={`w-full px-4 py-2 rounded-lg border font-medium transition-colors ${
            errors.category
              ? 'border-red-300 bg-red-50 text-neutral-900'
              : 'border-neutral-200 bg-white text-neutral-900 hover:border-neutral-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
          }`}
        >
          <option value="">{t('support.selectCategory')}</option>
          {categories.map((cat) => (
            <option key={cat.value} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select>
        {errors.category && <p className="mt-1 text-xs text-red-600">{errors.category}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          {t('support.message')} <span className="text-red-500">*</span>
        </label>
        <textarea
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            setErrors({ ...errors, message: '' });
          }}
          placeholder={t('support.messagePlaceholder')}
          rows={6}
          className={`w-full px-4 py-2 rounded-lg border font-medium transition-colors resize-none ${
            errors.message
              ? 'border-red-300 bg-red-50 text-neutral-900 placeholder-red-400'
              : 'border-neutral-200 bg-white text-neutral-900 placeholder-neutral-500 hover:border-neutral-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
          }`}
        />
        <div className="mt-1 flex items-center justify-between">
          <p className="text-xs text-neutral-500">{message.length}/2000</p>
          {errors.message && <p className="text-xs text-red-600">{errors.message}</p>}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={isLoading}
          className="px-6 py-2.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? t('support.loading') : t('support.submitBtn')}
        </button>
      </div>
    </form>
  );
}
